import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Chip } from "@/components/StatusChip";
import { AriaMark } from "@/components/AriaMark";
import { PolicyEditDialog } from "@/components/policies/PolicyEditDialog";
import { PolicyControls } from "@/components/policies/PolicyControls";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ShieldCheck, Loader2, Users, FlaskConical, DollarSign, CheckCircle2, Paperclip, X, FileText, Pencil, SlidersHorizontal } from "lucide-react";
import { api, fmtUSD, timeAgo } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  "No alcohol over $50",
  "All flights must be economy if under 6 hours",
  "Receipts required for anything over $25",
  "Rideshare capped at $75 unless airport transfer",
];

const GEN_STAGES = [
  "Aria is reading your policy…",
  "Converting to structured rule…",
  "Testing against 75 historical expenses…",
  "Estimating monthly impact…",
];

const SEVERITY_TONE = { low: "neutral", medium: "amber", high: "red" };

export default function Policies() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [genStage, setGenStage] = useState(0);
  const [result, setResult] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [controlsOpen, setControlsOpen] = useState(false);

  const loadPolicies = () => api.get("/policies").then((r) => setPolicies(r.data)).catch(() => {});
  useEffect(() => { loadPolicies(); }, []);

  useEffect(() => {
    if (!generating) return;
    setGenStage(0);
    const iv = setInterval(() => setGenStage((s) => Math.min(s + 1, GEN_STAGES.length - 1)), 2600);
    return () => clearInterval(iv);
  }, [generating]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File too large — 8MB max"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1];
      setAttachment({ name: f.name, b64, type: f.type === "application/pdf" ? "pdf" : "image" });
      toast.success(`Attached ${f.name} — Aria will read it`);
    };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if ((!text.trim() && !attachment) || generating) return;
    setGenerating(true);
    setResult(null);
    try {
      const payload = { text: text.trim() };
      if (attachment) {
        payload.attachment_base64 = attachment.b64;
        payload.attachment_type = attachment.type;
        payload.attachment_name = attachment.name;
      }
      const r = await api.post("/policies/generate", payload, { timeout: 120000 });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aria could not generate this policy");
    } finally {
      setGenerating(false);
    }
  };

  const enable = async () => {
    if (!result || saving) return;
    setSaving(true);
    try {
      const payload = {
        ...result.rule,
        estimated_monthly_impact: result.simulation.estimated_monthly_impact,
        affected_users: result.simulation.affected_users,
      };
      await api.post("/policies", { policy: payload, source_text: text.trim() });
      toast.success(`Policy enabled: ${result.rule.name}`);
      setResult(null);
      setText("");
      setAttachment(null);
      loadPolicies();
    } catch {
      toast.error("Could not enable policy");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    setPolicies((ps) => ps.map((x) => (x.id === p.id ? { ...x, enabled: !p.enabled } : x)));
    try {
      await api.patch(`/policies/${p.id}`, { enabled: !p.enabled });
      toast.success(`${p.name} ${!p.enabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Toggle failed");
      loadPolicies();
    }
  };

  return (
    <div className="space-y-6" data-testid="policies-page">
      {/* builder */}
      <div className="rounded-[14px] border border-[#222] bg-[#111111] p-4 card-shadow sm:p-6">
        <div className="flex items-center gap-2.5">
          <AriaMark size={32} />
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight">Policy Builder</h2>
            <p className="text-[11.5px] text-[#A3A3A3]">No forms. Describe the rule in plain English — Aria structures, tests and enforces it.</p>
          </div>
        </div>

        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="policy-builder-input"
          placeholder='Try: "No alcohol over $50 unless it&apos;s a client dinner"'
          className="mt-4 min-h-[110px] resize-none border-[#2a2a2a] bg-[#161616] text-[14px] text-white placeholder:text-[#555] focus-visible:ring-[rgba(79,142,247,0.4)]" />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => setText(ex)} data-testid="policy-example-chip"
              className="rounded-full border border-[#2a2a2a] bg-[#161616] px-3 py-1 text-[11px] text-[#A3A3A3] hover:border-[#3a3a3a] hover:text-white">
              {ex}
            </button>
          ))}
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden"
              onChange={onPickFile} data-testid="policy-attach-input" />
            {attachment && (
              <span className="flex items-center gap-1.5 rounded-full border border-[rgba(79,142,247,0.3)] bg-[rgba(79,142,247,0.1)] px-3 py-1 text-[11px] text-[#7FAEF9]"
                data-testid="policy-attachment-chip">
                <FileText size={11} />
                <span className="max-w-[160px] truncate">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} data-testid="policy-attachment-remove" className="text-[#6b6b6b] hover:text-white">
                  <X size={11} />
                </button>
              </span>
            )}
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="policy-attach-button"
              className="h-9 border-[#2a2a2a] bg-transparent px-3 text-[12.5px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
              <Paperclip size={13} className="mr-1.5" /> Attach file
            </Button>
            <Button onClick={generate} disabled={(!text.trim() && !attachment) || generating} data-testid="policy-generate-button"
              className="h-9 btn-gradient px-4 text-[13px] font-medium text-white disabled:opacity-40">
              {generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              {generating ? "Generating…" : "Generate policy"}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {generating && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden">
              <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-[rgba(79,142,247,0.2)] bg-[rgba(79,142,247,0.05)] px-4 py-3">
                <Loader2 size={13} className="animate-spin text-[#4F8EF7]" />
                <span className="text-[12.5px] text-[#7FAEF9]" data-testid="policy-generating-stage">{GEN_STAGES[genStage]}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* generated result */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-5 grid gap-4 lg:grid-cols-2" data-testid="policy-generated-result">
              {/* rule */}
              <div className="rounded-xl border border-[#242424] bg-[#141414] p-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold">{result.rule.name}</h3>
                  <Chip tone={SEVERITY_TONE[result.rule.severity] || "neutral"}>{result.rule.severity} severity</Chip>
                </div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#A3A3A3]">{result.rule.description}</p>
                <div className="mt-3 space-y-1.5">
                  {result.rule.conditions?.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-[#242424] bg-[#181818] px-3 py-1.5 text-[12px]">
                      <span className="font-medium text-[#7FAEF9]">{c.field}</span>
                      <span className="text-[#6b6b6b]">{c.operator}</span>
                      <span className="tabular-nums text-white">{String(c.value)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Chip tone="blue">action: {result.rule.action}</Chip>
                  <Chip tone="neutral">{result.rule.category}</Chip>
                  {(result.rule.applies_to?.departments || []).map((d) => <Chip key={d} tone="neutral">{d}</Chip>)}
                </div>
                {result.rule.exceptions?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Exceptions</div>
                    <ul className="mt-1 space-y-0.5">
                      {result.rule.exceptions.map((ex) => (
                        <li key={ex} className="text-[12px] text-[#c9c9c9]">• {ex}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* simulation */}
              <div className="flex flex-col rounded-xl border border-[#242424] bg-[#141414] p-5">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
                  <FlaskConical size={12} /> Tested against historical data
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-[#242424] bg-[#181818] p-3 text-center">
                    <div className="text-lg font-semibold tabular-nums">{result.simulation.historical_matches}</div>
                    <div className="text-[10px] text-[#6b6b6b]">would trigger</div>
                  </div>
                  <div className="rounded-lg border border-[#242424] bg-[#181818] p-3 text-center">
                    <div className="text-lg font-semibold tabular-nums">{result.simulation.affected_users}</div>
                    <div className="text-[10px] text-[#6b6b6b]">employees</div>
                  </div>
                  <div className="rounded-lg border border-[#242424] bg-[#181818] p-3 text-center">
                    <div className="text-lg font-semibold tabular-nums">{fmtUSD(result.simulation.estimated_monthly_impact)}</div>
                    <div className="text-[10px] text-[#6b6b6b]">monthly impact</div>
                  </div>
                </div>
                {result.simulation.sample_matches?.length > 0 && (
                  <div className="mt-3 overflow-hidden rounded-lg border border-[#242424]">
                    {result.simulation.sample_matches.map((m, i) => (
                      <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-[11.5px] ${i > 0 ? "border-t border-[#1e1e1e]" : ""}`}>
                        <span className="text-[#c9c9c9]">{m.merchant} · {m.employee_name}</span>
                        <span className="tabular-nums text-white">{fmtUSD(m.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Button onClick={enable} disabled={saving} data-testid="policy-enable-button"
                  className="mt-auto h-9 w-full bg-[#22C55E] text-[13px] font-medium text-black hover:bg-[#2fd66c]">
                  {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                  Enable policy
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* library */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-[14px] font-semibold tracking-tight">Policy library</h2>
          <Button variant="outline" onClick={() => setControlsOpen(true)} data-testid="policy-controls-button"
            className="h-8 border-0 bg-[#1c1c1c] px-3 text-[12px] text-[#A3A3A3] hover:bg-[#242424] hover:text-white">
            <SlidersHorizontal size={12} className="mr-1.5" /> Policy controls
          </Button>
        </div>
        {!policies ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-[14px] bg-[#141414]" />)}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="policy-library-grid">
            {policies.map((p) => (
              <motion.div key={p.id} whileHover={{ y: -2 }} transition={{ type: "spring", stiffness: 420, damping: 28 }}
                data-testid={`policy-card-${p.id}`}
                data-aria-key={`policy:${p.id}`} data-aria-label={`Policy: ${p.name}`}
                className={`card-lift rounded-[14px] border bg-[#111111] p-5 card-shadow ${p.enabled ? "border-[#222]" : "border-[#1a1a1a] opacity-60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(79,142,247,0.1)]">
                      <ShieldCheck size={13} className="text-[#7FAEF9]" />
                    </div>
                    <div className="text-[13px] font-medium leading-tight">{p.name}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEditing(p)} data-testid={`policy-edit-${p.id}`} title="Edit policy"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6b6b6b] transition-colors hover:bg-[#1c1c1c] hover:text-white">
                      <Pencil size={12} />
                    </button>
                    <Switch checked={p.enabled} onCheckedChange={() => toggle(p)} data-testid={`policy-toggle-${p.id}`}
                      className="data-[state=checked]:bg-[#4F8EF7]" />
                  </div>
                </div>
                <p className="mt-2 line-clamp-2 text-[11.5px] leading-relaxed text-[#A3A3A3]">{p.description}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Chip tone="neutral">{p.category}</Chip>
                  <Chip tone={SEVERITY_TONE[p.severity] || "neutral"}>{p.severity}</Chip>
                  <Chip tone={p.violations_count > 8 ? "red" : p.violations_count > 3 ? "amber" : "green"}>
                    {p.violations_count} violations
                  </Chip>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-[#1c1c1c] pt-2.5 text-[10.5px] text-[#6b6b6b]">
                  <span className="flex items-center gap-1"><Users size={10} /> {p.owner}</span>
                  {p.last_triggered ? (
                    <button
                      onClick={() => navigate(`/expenses?policy=${p.id}&policy_status=triggered`)}
                      data-testid={`policy-triggered-link-${p.id}`}
                      title="View all expenses this policy triggered on"
                      className="text-[#7FAEF9] transition-colors hover:text-white hover:underline">
                      Last triggered {timeAgo(p.last_triggered)} →
                    </button>
                  ) : (
                    <span>Never triggered</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <PolicyEditDialog policy={editing} open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }} onSaved={loadPolicies} />
      <Dialog open={controlsOpen} onOpenChange={setControlsOpen}>
        <DialogContent className="max-h-[86vh] overflow-y-auto border-[#242424] bg-[#111111] text-white sm:max-w-[480px]" data-testid="policy-controls-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold tracking-tight">Policy controls</DialogTitle>
            <DialogDescription className="text-[12px] text-[#A3A3A3]">
              Governance for who can create and edit policies. Also available in Settings → Policy Controls.
            </DialogDescription>
          </DialogHeader>
          <PolicyControls />
        </DialogContent>
      </Dialog>
    </div>
  );
}
